import React, { useContext, useState } from "react";

import {
  getDateRangeByValue,
  useStoredView,
} from "@pushpress/shared-components";
import { toast } from "react-toastify";

import { AppContext } from "../../../context/app";
import { getCalendarItemsListFilterTagsMap } from "./CalendarItemsListFilter.utils";
import { useListCalendarItemsQuery } from "./CalendarItemsList-generated.hooks";
import { defaultToastOptions, extractError } from "../../../utils/misc";

import type { DataTableSort } from "@pushpress/shared-components";

import CalendarItemsListHeader from "./CalendarItemsListHeader";
import CalendarItemsListTable from "./CalendarItemsListTable";
import { CalendarItemSessionTypes } from "../Calendar.utils";
import {
  DateRangeValue,
  DateRange,
} from "@pushpress/shared-components/build/components/selectors/DateRangeSelector/DateRangeSelector.types";
import CalendarActionsRouter from "../CalendarActionsRouter";
import { useRouteMatch } from "react-router-dom";
import { isCypress } from "../../../utils/cypress";

interface CalendarItemsListProps {
  calendarItemType: CalendarItemSessionTypes;
}

function CalendarItemsList({ calendarItemType }: CalendarItemsListProps) {
  const routeMatch = useRouteMatch();
  const path = isCypress ? `/schedule/list` : routeMatch?.path;

  const app = useContext(AppContext);
  const clientUuid = app.client.uuid || app.client.client_uuid;

  const [dateRangeValue, setDateRangeValue] =
    useState<DateRangeValue>("next-30-days");
  const dateRangeRaw: DateRange = getDateRangeByValue(
    dateRangeValue,
    app.client.timezone,
  );
  const dateRange: DateRange = {
    // Have to strip off time portion because getDateRangeByValue sometimes returns local times, sometimes returns UTC
    start: dateRangeRaw.start.substring(0, dateRangeRaw.start.indexOf("T")),
    end: dateRangeRaw.end.substring(0, dateRangeRaw.end.indexOf("T")),
  };

  const {
    filter,
    filterTagOptions,
    viewSaved,
    handleFilterChange,
    handleFilterTagDelete,
    handleViewSave,
  } = useStoredView({
    // per https://www.typescriptlang.org/docs/handbook/enums.html#reverse-mappings
    viewSaveId: `${CalendarItemSessionTypes[calendarItemType]}-list-filter`,
    getFilterTagsMap: getCalendarItemsListFilterTagsMap,
  });

  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<DataTableSort<string>>({
    key: "startDatetime",
    order: "asc",
  });

  const { data, loading, refetch } = useListCalendarItemsQuery({
    variables: {
      // Ignoring browser's timezone as we always query CalendarItem times as if they were local
      //   (see https://www.notion.so/pushpress/DateTimes-and-Timezones-in-the-Graph-297222589b324242ab59564d635790aa?pvs=4)
      listCalendarItemsInput: {
        clientUuid: clientUuid,
        calendarSessionTypeId: {
          value: calendarItemType,
          operator: "EQ",
        },
        // filters
        startDate: dateRange.start,
        endDate: dateRange.end,
        calendarItemTypeUuids:
          filter?.types && filter.types.length > 0
            ? filter.types.map((itemType) => itemType.uuid)
            : undefined,
        coachUuids:
          filter?.staff && filter.staff.length > 0
            ? filter.staff.map((itemStaff) => itemStaff.userUuid)
            : undefined,
        locationUuid: undefined,
        // pagination
        offset: page - 1,
        limit: 20,
        page: undefined,
        // sorting
        sortBy: {
          field: sort.key,
          type: sort.order === "asc" ? "ASC" : "DESC",
        },
        isPublicOnly: false,
      },
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    onError(error) {
      toast.error(extractError(error), defaultToastOptions);
    },
  });

  // fetchPolicy 'cache-and-network' returns data from cache if available while getting data from
  //   the network. This allows us to show a cached version to the user quicker while getting
  //   possibly newer data from the network.  If there is newer data, we will get another render
  //   with the updated data.  BUT, loading goes to true even if it returns the data from the cache
  //   so we have to only show the loading when we don't have data.
  const showLoadingScreen = loading && !data;

  return (
    <>
      <CalendarActionsRouter refetch={refetch} baseUrl={path} />
      <CalendarItemsListHeader calendarItemType={calendarItemType} />
      <div className="mt-4">
        <CalendarItemsListTable
          calendarItemType={calendarItemType}
          data={data}
          loading={showLoadingScreen}
          viewSaved={viewSaved}
          dateRangeValue={dateRangeValue}
          filter={filter}
          filterTagOptions={filterTagOptions}
          page={page}
          sort={sort}
          handleFilterChange={handleFilterChange}
          handleFilterTagDelete={handleFilterTagDelete}
          handleViewSave={handleViewSave}
          setDateRangeValue={setDateRangeValue}
          setPage={setPage}
          setSort={setSort}
          refetch={refetch}
        />
      </div>
    </>
  );
}

export default CalendarItemsList;
